import { useCallback } from "react";
import html2canvas from "html2canvas";

interface ChartCaptureResult {
  dataUrl: string;
  width: number;
  height: number;
}

interface UseChartCaptureReturn {
  captureChart: (element: HTMLElement) => Promise<ChartCaptureResult>;
  captureChartById: (elementId: string) => Promise<ChartCaptureResult | null>;
  captureMultipleCharts: (elementIds: string[]) => Promise<Map<string, ChartCaptureResult>>;
}

/**
 * Hook for capturing chart elements as images for PDF generation
 */
export const useChartCapture = (): UseChartCaptureReturn => {
  const captureChart = useCallback(
    async (element: HTMLElement): Promise<ChartCaptureResult> => {
      try {
        const canvas = await html2canvas(element, {
          scale: 2, // Higher scale for better quality
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: false,
        });

        return {
          dataUrl: canvas.toDataURL("image/png"),
          width: canvas.width,
          height: canvas.height,
        };
      } catch (error) {
        console.error("Error capturing chart:", error);
        throw error;
      }
    },
    [],
  );

  const captureChartById = useCallback(
    async (elementId: string): Promise<ChartCaptureResult | null> => {
      const element = document.getElementById(elementId);

      if (!element) {
        console.warn(`Element with id "${elementId}" not found`);

return null;
      }

return captureChart(element);
    },
    [captureChart],
  );

  const captureMultipleCharts = useCallback(
    async (elementIds: string[]): Promise<Map<string, ChartCaptureResult>> => {
      const results = new Map<string, ChartCaptureResult>();

      for (const id of elementIds) {
        const result = await captureChartById(id);

        if (result) {
          results.set(id, result);
        }
      }

      return results;
    },
    [captureChartById],
  );

  return {
    captureChart,
    captureChartById,
    captureMultipleCharts,
  };
};

/**
 * Chart IDs used in the paygap analysis page
 */
export const PAYGAP_CHART_IDS = {
  payDifferenceGauge: "paygap-pay-difference-gauge",
  incomeQuartileBoxPlot: "paygap-income-quartile-boxplot",
  employeeDistributionDonut: "paygap-employee-distribution-donut",
  salaryCostChart: "paygap-salary-cost-chart",
  seniorityPayGapChart: "paygap-seniority-chart-pdf",
  positionRoleChart: "paygap-position-role-chart-pdf",
  gradePayGapChart: "paygap-grade-chart",
  gradePayGapChartGrading1: "paygap-grade-chart-grading1",
} as const;

export type PaygapChartId = (typeof PAYGAP_CHART_IDS)[keyof typeof PAYGAP_CHART_IDS];
